import { PageHeader, PageHeaderContent, PageHeaderMain, PageHeaderTitle } from '../page-header';
import { cn, type CompositionProp } from '@utils';

type PrimaryPageHeaderProps = React.PropsWithChildren<{
	title: CompositionProp;
	breadcrumbs?: React.ReactNode;
	sideContent?: React.ReactNode;
	tabs?: React.ReactNode;
	className?: string;
	hasMinHeight?: boolean;
}>;

/**
 * Compact headers and tabless pages need explicit bottom padding. Pages with tabs
 * already get spacing from the tabs row, so default min-height headers skip `pb-5`.
 */
const shouldApplyBottomPadding = (hasMinHeight: boolean, tabs: React.ReactNode): boolean =>
	!hasMinHeight || !tabs;

export const PrimaryPageHeader: React.FC<PrimaryPageHeaderProps> = ({
	title,
	breadcrumbs,
	sideContent,
	tabs,
	className,
	hasMinHeight = true,
	...props
}) => {
	const titleComponent =
		typeof title === 'string' ? (
			<PageHeaderTitle className="text-primary">{title}</PageHeaderTitle>
		) : (
			title
		);

	return (
		<PageHeader {...props} className={cn(hasMinHeight && 'min-h-sm', className)}>
			<PageHeaderContent className={cn(shouldApplyBottomPadding(hasMinHeight, tabs) && 'pb-5', 'min-h-full')}>
				{breadcrumbs}
				<PageHeaderMain>
					<div className="max-w-full">{titleComponent}</div>
					{sideContent && <div className="flex items-center">{sideContent}</div>}
				</PageHeaderMain>
				{tabs ? <div data-testid="page-header-tabs">{tabs}</div> : null}
			</PageHeaderContent>
		</PageHeader>
	);
};
